import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const heroProducts = [
  {
    id: "double-eagle",
    image: "/images/products/double-eagle.png",
    alt: "Goldnote 1 Gram Fine Gold 9999 - Double Eagle"
  },
  {
    id: "year-of-horse",
    image: "/images/products/year-of-horse.png",
    alt: "Goldnote 1 Gram Fine Gold 9999 - Year of the Horse 2026"
  },
  {
    id: "electrum",
    image: "/images/products/electrum.png",
    alt: "Goldnote Electrum Limited Edition - 1 Gram Gold + 5 Grams Silver"
  },
  {
    id: "genesis-series",
    image: "/images/products/genesis-series.png",
    alt: "Goldnote 10 Grams Fine Gold 9999 - Genesis Series Limited Edition"
  }
];

export const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroProducts.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const scrollToContent = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: "smooth",
    });
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section - Above the Fold */}
      <section className="hero-section" data-testid="hero-section">
        {/* Background Image */}
        <div 
          className="hero-bg"
          style={{
            backgroundImage: `url('/images/backgrounds/gold-bars-bg.png')`,
          }}
        />
        
        {/* Overlays */}
        <div className="hero-overlay" />
        <div className="hero-vignette" />
        <div className="hero-gold-glow" />

        {/* Content */}
        <div className="hero-content flex flex-col items-center justify-center">
          {/* Premium Badge */}
          <div className="hero-badge animate-fade-in">
            <span className="hero-badge-line" />
            <span className="text-[0.625rem] tracking-[0.3em] uppercase text-[#D4AF37]">
              Premium Gold Notes
            </span>
            <span className="hero-badge-line" />
          </div>
          
          <h1 className="hero-title animate-fade-in-up-delay-1">
            <span className="hero-title-main">Pure Gold</span>
            <span className="hero-title-sub">Reimagined</span>
          </h1>

          {/* Product Image Carousel */}
          <div className="animate-fade-in-up-delay-2 -mt-4 mb-4 relative w-[350px] h-[220px]">
            {heroProducts.map((product, index) => (
              <img 
                key={product.id}
                src={product.image}
                alt={product.alt}
                className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-1000 ease-in-out ${
                  index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
                style={{ filter: 'drop-shadow(0 20px 40px rgba(212, 175, 55, 0.25))' }}
              />
            ))}
            {/* Carousel Indicators */}
            <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-2">
              {heroProducts.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    index === currentSlide 
                      ? 'bg-[#D4AF37] w-8' 
                      : 'bg-white/30 hover:bg-white/50 w-1.5'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          <p className="hero-subtitle animate-fade-in-up-delay-2 mt-2 mb-6">
            99.99% pure gold in a revolutionary polymer note format.
          </p>

          <div className="hero-cta-group animate-fade-in-up-delay-3">
            <div className="hero-buttons">
              <Link 
                to="/products" 
                className="hero-btn-primary"
                data-testid="hero-products-btn"
              >
                Explore Collection <ArrowRight size={18} />
              </Link>
              <Link 
                to="/about" 
                className="hero-btn-secondary"
                data-testid="hero-about-btn"
              >
                Our Story <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Gold Gradient Divider */}
      <div 
        className="h-2 md:h-3 relative"
        style={{
          background: 'linear-gradient(90deg, #000000 0%, #B08D1A 25%, #FFFFFF 50%, #B08D1A 75%, #000000 100%)'
        }}
      >
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, #000000 0%, #B08D1A 25%, #FFFFFF 50%, #B08D1A 75%, #000000 100%)',
            mixBlendMode: 'overlay'
          }}
        />
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, #000000 0%, #B08D1A 25%, #FFFFFF 50%, #B08D1A 75%, #000000 100%)',
            mixBlendMode: 'overlay'
          }}
        />
      </div>

      {/* Now Available In Section */}
      <section className="py-12 md:py-16" style={{ backgroundColor: '#1a1405' }}>
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center">
            <p className="text-[0.625rem] tracking-[0.3em] uppercase text-[#D4AF37]/80 mb-4">
              Shop Now
            </p>
            <h2 className="text-2xl md:text-3xl tracking-[0.05em] text-white/90 mb-8" style={{ fontFamily: 'Cinzel, serif' }}>
              Now Available In
            </h2>
            
            <div className="flex flex-wrap justify-center gap-3 md:gap-4">
              {/* USA - JM Bullion */}
              <Link 
                to="/retailers/jm-bullion"
                className="flex flex-col items-center gap-1.5 px-5 py-3 bg-[#0A0A0A] border border-[#D4AF37]/30 rounded hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all duration-300 group"
              >
                <div className="flex items-center gap-2">
                  <img src="/images/flags/us.png" alt="USA" className="w-6 h-4 object-cover rounded-sm" />
                  <span className="text-sm tracking-wider text-white/70 group-hover:text-[#D4AF37] transition-colors">USA</span>
                </div>
                <span className="text-[0.6rem] tracking-wider uppercase text-[#D4AF37]/60 group-hover:text-[#D4AF37] transition-colors">JM Bullion</span>
              </Link>
              
              {/* Canada - TD */}
              <Link 
                to="/retailers/td"
                className="flex flex-col items-center gap-1.5 px-5 py-3 bg-[#0A0A0A] border border-[#D4AF37]/30 rounded hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all duration-300 group"
              >
                <div className="flex items-center gap-2">
                  <img src="/images/flags/ca.png" alt="Canada" className="w-6 h-4 object-cover rounded-sm" />
                  <span className="text-sm tracking-wider text-white/70 group-hover:text-[#D4AF37] transition-colors">Canada</span>
                </div>
                <span className="text-[0.6rem] tracking-wider uppercase text-[#D4AF37]/60 group-hover:text-[#D4AF37] transition-colors">TD Precious Metals</span>
              </Link>
              
              {/* UK - Coming Soon */}
              <div className="flex flex-col items-center gap-1.5 px-5 py-3 bg-[#0A0A0A]/50 border border-white/10 rounded opacity-50 cursor-not-allowed">
                <div className="flex items-center gap-2">
                  <img src="/images/flags/gb.png" alt="UK" className="w-6 h-4 object-cover rounded-sm grayscale" />
                  <span className="text-sm tracking-wider text-white/40">UK</span>
                </div>
                <span className="text-[0.6rem] tracking-wider text-white/30 uppercase">Coming Soon</span>
              </div>
              
              {/* Australia - Coming Soon */}
              <div className="flex flex-col items-center gap-1.5 px-5 py-3 bg-[#0A0A0A]/50 border border-white/10 rounded opacity-50 cursor-not-allowed">
                <div className="flex items-center gap-2">
                  <img src="/images/flags/au.png" alt="Australia" className="w-6 h-4 object-cover rounded-sm grayscale" />
                  <span className="text-sm tracking-wider text-white/40">AUS</span>
                </div>
                <span className="text-[0.6rem] tracking-wider text-white/30 uppercase">Coming Soon</span>
              </div>
              
              {/* EU - Coming Soon */}
              <div className="flex flex-col items-center gap-1.5 px-5 py-3 bg-[#0A0A0A]/50 border border-white/10 rounded opacity-50 cursor-not-allowed">
                <div className="flex items-center gap-2">
                  <img src="/images/flags/eu.png" alt="EU" className="w-6 h-4 object-cover rounded-sm grayscale" />
                  <span className="text-sm tracking-wider text-white/40">EU</span>
                </div>
                <span className="text-[0.6rem] tracking-wider text-white/30 uppercase">Coming Soon</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gold Gradient Divider - Below Now Available */}
      <div 
        className="h-2 md:h-3 relative"
        style={{
          background: 'linear-gradient(90deg, #000000 0%, #B08D1A 25%, #FFFFFF 50%, #B08D1A 75%, #000000 100%)'
        }}
      >
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, #000000 0%, #B08D1A 25%, #FFFFFF 50%, #B08D1A 75%, #000000 100%)',
            mixBlendMode: 'overlay'
          }}
        />
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, #000000 0%, #B08D1A 25%, #FFFFFF 50%, #B08D1A 75%, #000000 100%)',
            mixBlendMode: 'overlay'
          }}
        />
      </div>

      {/* Product Diagram Section */}
      <section className="bg-black py-12 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-[0.625rem] tracking-[0.3em] uppercase text-[#D4AF37]/80 mb-4">
              Anatomy of Excellence
            </p>
            <h2 className="text-3xl md:text-4xl tracking-[0.05em] text-white/90" style={{ fontFamily: 'Cinzel, serif' }}>
              The Goldnote
            </h2>
          </div>
          
          <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-8">
            {/* Diagram Image - Left Aligned */}
            <div className="lg:w-3/5 w-full">
              {/* Mobile: Different image layout */}
              <img 
                src="/images/goldnote-diagram-mobile.png" 
                alt="Goldnote Product Features Diagram" 
                className="lg:hidden w-full"
              />
              {/* Desktop: Normal image */}
              <img 
                src="/images/goldnote-diagram.png" 
                alt="Goldnote Product Features Diagram" 
                className="hidden lg:block w-full"
              />
            </div>
            
            {/* Feature Labels - Right Side / Below on Mobile */}
            <div className="lg:w-2/5 w-full flex flex-col gap-3 lg:gap-4 justify-center">
              {/* Arrow 1 - Serial Number */}
              <div className="relative p-4 lg:p-5 bg-gradient-to-br from-[#0A0A0A] to-[#0F0F0F] border border-[#D4AF37]/20 rounded overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#D4AF37] to-[#D4AF37]/20" />
                <div className="flex items-start gap-3 lg:gap-4 pl-2 lg:pl-3">
                  <span className="text-[#D4AF37] text-xl lg:text-2xl font-serif opacity-60">01</span>
                  <div>
                    <h4 className="text-[#D4AF37] text-[0.65rem] lg:text-xs tracking-[0.2em] uppercase mb-1 lg:mb-2">Unique Serial Number</h4>
                    <p className="text-white/60 text-xs lg:text-sm font-light leading-relaxed">
                      Each note features a unique proof number for authenticity verification.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Arrow 2 - Gold Ingot */}
              <div className="relative p-4 lg:p-5 bg-gradient-to-br from-[#0A0A0A] to-[#0F0F0F] border border-[#D4AF37]/20 rounded overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#D4AF37] to-[#D4AF37]/20" />
                <div className="flex items-start gap-3 lg:gap-4 pl-2 lg:pl-3">
                  <span className="text-[#D4AF37] text-xl lg:text-2xl font-serif opacity-60">02</span>
                  <div>
                    <h4 className="text-[#D4AF37] text-[0.65rem] lg:text-xs tracking-[0.2em] uppercase mb-1 lg:mb-2">1 Gram Fine Gold 9999</h4>
                    <p className="text-white/60 text-xs lg:text-sm font-light leading-relaxed">
                      Pure 24-karat gold securely embedded within the polymer substrate.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Arrow 3 - Security Feature */}
              <div className="relative p-4 lg:p-5 bg-gradient-to-br from-[#0A0A0A] to-[#0F0F0F] border border-[#D4AF37]/20 rounded overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#D4AF37] to-[#D4AF37]/20" />
                <div className="flex items-start gap-3 lg:gap-4 pl-2 lg:pl-3">
                  <span className="text-[#D4AF37] text-xl lg:text-2xl font-serif opacity-60">03</span>
                  <div>
                    <h4 className="text-[#D4AF37] text-[0.65rem] lg:text-xs tracking-[0.2em] uppercase mb-1 lg:mb-2">Advanced Security</h4>
                    <p className="text-white/60 text-xs lg:text-sm font-light leading-relaxed">
                      Multi-layer holographic elements and micro-printing protection.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Arrow 4 - Bottom Feature */}
              <div className="relative p-4 lg:p-5 bg-gradient-to-br from-[#0A0A0A] to-[#0F0F0F] border border-[#D4AF37]/20 rounded overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#D4AF37] to-[#D4AF37]/20" />
                <div className="flex items-start gap-3 lg:gap-4 pl-2 lg:pl-3">
                  <span className="text-[#D4AF37] text-xl lg:text-2xl font-serif opacity-60">04</span>
                  <div>
                    <h4 className="text-[#D4AF37] text-[0.65rem] lg:text-xs tracking-[0.2em] uppercase mb-1 lg:mb-2">QR Authentication</h4>
                    <p className="text-white/60 text-xs lg:text-sm font-light leading-relaxed">
                      Smartphone-scannable QR code for instant verification.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gold Gradient Divider - Above Features */}
      <div 
        className="h-2 md:h-3 relative"
        style={{
          background: 'linear-gradient(90deg, #000000 0%, #B08D1A 25%, #FFFFFF 50%, #B08D1A 75%, #000000 100%)'
        }}
      >
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, #000000 0%, #B08D1A 25%, #FFFFFF 50%, #B08D1A 75%, #000000 100%)',
            mixBlendMode: 'overlay'
          }}
        />
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, #000000 0%, #B08D1A 25%, #FFFFFF 50%, #B08D1A 75%, #000000 100%)',
            mixBlendMode: 'overlay'
          }}
        />
      </div>

      {/* Black Section - Features */}
      <section 
        className="relative py-24 overflow-hidden" 
        data-testid="features-section"
        style={{ backgroundColor: '#1a1405' }}
      >
        {/* Faint Background Image - Only in this section */}
        <div 
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage: 'url(/images/features-bg.png)',
            backgroundSize: '150%',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed'
          }}
        />
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <p className="text-[0.625rem] tracking-[0.3em] uppercase text-[#D4AF37]/80 mb-4">
              Innovation & Security
            </p>
            <h2 className="text-3xl md:text-4xl tracking-[0.05em] text-white/90" style={{ fontFamily: 'Cinzel, serif' }}>
              Features
            </h2>
          </div>

          {/* Two Column Layout - Feature Card + Video */}
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
            {/* Feature Card */}
            <div className="relative p-8 md:p-12 bg-gradient-to-br from-[#0A0A0A] to-[#111111] border border-[#D4AF37]/20 rounded-lg overflow-hidden">
              {/* Gold accent corner */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#D4AF37]/10 to-transparent" />
              
              {/* Icon */}
              <div className="w-16 h-16 mb-6 border border-[#D4AF37]/40 rounded-lg flex items-center justify-center bg-[#D4AF37]/5">
                <svg className="w-8 h-8 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
              </div>

              {/* Content */}
              <h3 className="text-xl md:text-2xl text-white/90 mb-4 tracking-wide" style={{ fontFamily: 'Cinzel, serif' }}>
                Secure QR Authentication
              </h3>
              <p className="text-white/60 font-light leading-relaxed mb-8">
                Each Goldnote features a smartphone-readable encrypted QR code for rapid and secure authentication, ensuring the legitimacy of your investment.
              </p>

              {/* CTA Button */}
              <a 
                href="#" 
                className="hero-btn-primary inline-flex items-center gap-2"
              >
                Learn More <ArrowRight size={18} />
              </a>

              {/* Bottom gold line accent */}
              <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#D4AF37]/50 to-transparent" />
            </div>

            {/* YouTube Video with Gold Lines */}
            <div className="flex flex-col h-full">
              {/* Top Gold-Black Line */}
              <div className="h-[3px] rounded-full" style={{
                background: 'linear-gradient(90deg, #000000 0%, #D4AF37 50%, #000000 100%)'
              }} />
              
              {/* Video Container - fills remaining space */}
              <div className="flex-1 flex items-center py-4">
                <div className="relative w-full aspect-video rounded-lg overflow-hidden border border-[#D4AF37]/30 bg-black shadow-lg shadow-black/50">
                  <iframe 
                    className="absolute inset-0 w-full h-full"
                    src="https://www.youtube.com/embed/fzKTvVI19VI?si=LWpydlrrs2sBrNiY" 
                    title="Goldnote Video" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                    referrerPolicy="strict-origin-when-cross-origin" 
                    allowFullScreen
                  />
                </div>
              </div>
              
              {/* Bottom Gold-Black Line */}
              <div className="h-[3px] rounded-full" style={{
                background: 'linear-gradient(90deg, #000000 0%, #D4AF37 50%, #000000 100%)'
              }} />
            </div>
          </div>
        </div>
      </section>

      {/* Gold Gradient Divider - Above CTA */}
      <div 
        className="h-2 md:h-3 relative"
        style={{
          background: 'linear-gradient(90deg, #000000 0%, #B08D1A 25%, #FFFFFF 50%, #B08D1A 75%, #000000 100%)'
        }}
      >
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, #000000 0%, #B08D1A 25%, #FFFFFF 50%, #B08D1A 75%, #000000 100%)',
            mixBlendMode: 'overlay'
          }}
        />
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, #000000 0%, #B08D1A 25%, #FFFFFF 50%, #B08D1A 75%, #000000 100%)',
            mixBlendMode: 'overlay'
          }}
        />
      </div>

      {/* CTA Section */}
      <section className="bg-black py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[0.625rem] tracking-[0.3em] uppercase text-[#D4AF37]/80 mb-4">
              Start Your Collection
            </p>
            <h2 className="text-3xl md:text-5xl tracking-[0.02em] text-white/90 mb-6" style={{ fontFamily: 'Cinzel, serif' }}>
              Own a Piece of History
            </h2>
            <p className="text-white/60 font-light leading-relaxed mb-10 max-w-2xl mx-auto">
              Each Goldnote represents a perfect fusion of artistry, innovation, and tangible value. 
              Begin your journey into the future of precious metal ownership today.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link 
                to="/products" 
                className="hero-btn-primary inline-flex items-center gap-2"
              >
                Shop Collection <ArrowRight size={18} />
              </Link>
              <a 
                href="mailto:info@goldnote.com" 
                className="hero-btn-secondary inline-flex items-center gap-2"
              >
                Contact Us <ArrowRight size={18} />
              </a>
            </div>
            
            {/* Trust Indicators */}
            <div className="mt-16 pt-10 border-t border-white/5">
              <div className="flex flex-wrap justify-center gap-8 md:gap-16">
                <div className="text-center">
                  <p className="text-2xl md:text-3xl text-[#D4AF37] font-serif mb-1">9999</p>
                  <p className="text-[0.625rem] tracking-[0.2em] uppercase text-white/40">Pure Gold</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl md:text-3xl text-[#D4AF37] font-serif mb-1">U.S.</p>
                  <p className="text-[0.625rem] tracking-[0.2em] uppercase text-white/40">Patented</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl md:text-3xl text-[#D4AF37] font-serif mb-1">QR</p>
                  <p className="text-[0.625rem] tracking-[0.2em] uppercase text-white/40">Authenticated</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl md:text-3xl text-[#D4AF37] font-serif mb-1">100%</p>
                  <p className="text-[0.625rem] tracking-[0.2em] uppercase text-white/40">Secure</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
